import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment, hasPaymentsConfigured } from "@/lib/stripe";
import { monthlyEquivalent } from "./format";
import type { CategoryValue, BillingCycle, SalaryEntryType } from "./categories";
import type { Tables, TablesInsert } from "@/integrations/supabase/types";

/**
 * Row from `subscriptions`. `monthly_cost_nok` is in the row's own currency —
 * never sum across rows. Use `monthly_cost_nok_equiv` for aggregates. The
 * `category` / `billing_cycle` fields are narrowed to the app's local unions.
 */
export type Subscription = Omit<Tables<"subscriptions">, "category" | "billing_cycle"> & {
  category: CategoryValue;
  billing_cycle: BillingCycle;
};

export type CurrencyRate = Pick<Tables<"currency_rates">, "currency" | "nok_per_unit">;

export type SalaryEntry = Omit<Tables<"salary_entries">, "entry_type"> & {
  entry_type?: SalaryEntryType | string;
};

export type InflationRateKind = "arsvekst" | "tolvmanedersendring";

/**
 * `rate_kind = "arsvekst"` → official annual average-of-year KPI growth
 * (SSB publishes ~9-10 January the following year).
 * `rate_kind = "tolvmanedersendring"` → twelve-month change from the latest
 * published month; provisional until the arsvekst figure exists. `as_of_month`
 * (1-12) is only meaningful for the twelve-month variant.
 */
export type InflationRate = Omit<Tables<"inflation_rates">, "rate_kind" | "source"> & {
  rate_kind: InflationRateKind;
  source?: string;
};

export type CatalogItem = Omit<Tables<"subscription_catalog">, "category"> & {
  category: CategoryValue;
};

export type ServiceCategory = Omit<Tables<"service_categories">, "key"> & {
  key: CategoryValue;
};

export interface ServiceWithCategory {
  id: string;
  name: string;
  category_id: string;
  category_key: CategoryValue;
  category_label: string;
  country: string;
}

export type ServicePlan = Omit<Tables<"service_plans">, "billing_cycle"> & {
  billing_cycle: BillingCycle;
};

export function useIsAdmin() {
  return useQuery({
    queryKey: ["is-admin"],
    queryFn: async (): Promise<boolean> => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return false;
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", u.user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (error) return false;
      return !!data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

export type BillingPlan = "free" | "pro";

export interface BillingStatus {
  plan: BillingPlan;
  planLabel: string;
  isActive: boolean;
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  priceId: string | null;
  productId: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  environment: string | null;
}

const FREE_BILLING_STATUS: BillingStatus = {
  plan: "free",
  planLabel: "Free",
  isActive: false,
  status: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  priceId: null,
  productId: null,
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  environment: null,
};

export function useBillingStatus() {
  const qc = useQueryClient();
  const env = hasPaymentsConfigured() ? getStripeEnvironment() : null;
  const q = useQuery({
    queryKey: ["billing-status", env],
    queryFn: async (): Promise<BillingStatus> => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user || !env) return FREE_BILLING_STATUS;
      const { data, error } = await supabase
        .from("subscriptions_billing")
        .select("*")
        .eq("user_id", u.user.id)
        .eq("environment", env)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error || !data) return FREE_BILLING_STATUS;

      const now = Date.now();
      const periodEndMs = data.current_period_end
        ? new Date(data.current_period_end).getTime()
        : null;
      const status = data.status ?? "";
      const notExpired = periodEndMs === null || periodEndMs > now;
      const isActive =
        (["active", "trialing", "past_due"].includes(status) && notExpired) ||
        (status === "canceled" && periodEndMs !== null && periodEndMs > now);

      return {
        plan: isActive ? "pro" : "free",
        planLabel: isActive ? "Pro" : "Free",
        isActive,
        status: status || null,
        currentPeriodEnd: data.current_period_end ?? null,
        cancelAtPeriodEnd: Boolean(data.cancel_at_period_end),
        priceId: data.price_id ?? null,
        productId: data.product_id ?? null,
        stripeCustomerId: data.stripe_customer_id ?? null,
        stripeSubscriptionId: data.stripe_subscription_id ?? null,
        environment: data.environment ?? null,
      };
    },
    staleTime: 1000 * 30,
  });

  // Realtime: refetch billing when the webhook writes a change for this user.
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (cancelled || !u.user) return;
      channel = supabase
        .channel(`billing:${u.user.id}:${Math.random().toString(36).slice(2)}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "subscriptions_billing",
            filter: `user_id=eq.${u.user.id}`,
          },
          () => {
            qc.invalidateQueries({ queryKey: ["billing-status"] });
          },
        )
        .subscribe();
    })();
    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [qc]);

  return q;
}

export function useAllPlans() {
  return useQuery({
    queryKey: ["all-plans"],
    queryFn: async (): Promise<ServicePlan[]> => {
      const { data, error } = await supabase
        .from("service_plans")
        .select(
          "id,service_id,name,monthly_price_nok,billing_cycle,sort_order,currency,is_active,source_url,last_verified_at,notes",
        );
      if (error) throw error;
      return (data ?? []) as ServicePlan[];
    },
    staleTime: 1000 * 60 * 10,
  });
}

export function useSubscriptions() {
  return useQuery({
    queryKey: ["subscriptions"],
    queryFn: async (): Promise<Subscription[]> => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .order("monthly_cost_nok_equiv", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Subscription[];
    },
  });
}

export function useCatalog() {
  return useQuery({
    queryKey: ["catalog"],
    queryFn: async (): Promise<CatalogItem[]> => {
      const { data, error } = await supabase
        .from("subscription_catalog")
        .select("*")
        .order("is_norwegian", { ascending: false })
        .order("name");
      if (error) throw error;
      return (data ?? []) as CatalogItem[];
    },
    staleTime: 1000 * 60 * 60,
  });
}

export function useServiceCatalog(country: string = "NO") {
  return useQuery({
    queryKey: ["service-catalog", country],
    queryFn: async (): Promise<ServiceWithCategory[]> => {
      const { data, error } = await supabase
        .from("services")
        .select("id,name,category_id,country, service_categories!inner(key,label,sort_order)")
        .eq("is_active", true)
        .eq("country", country)
        .order("name");
      if (error) throw error;
      type ServiceRow = {
        id: string;
        name: string;
        category_id: string;
        country: string;
        service_categories: { key: string; label: string; sort_order: number };
      };
      return ((data ?? []) as unknown as ServiceRow[]).map((r) => ({
        id: r.id,
        name: r.name,
        category_id: r.category_id,
        country: r.country,
        category_key: r.service_categories.key as CategoryValue,
        category_label: r.service_categories.label,
      }));
    },
    staleTime: 1000 * 60 * 60,
  });
}

export function useAvailableCountries() {
  return useQuery({
    queryKey: ["service-countries"],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("services")
        .select("country")
        .eq("is_active", true);
      if (error) throw error;
      const set = new Set<string>();
      for (const r of (data ?? []) as Array<{ country: string }>) set.add(r.country);
      return Array.from(set).sort();
    },
    staleTime: 1000 * 60 * 60,
  });
}

export function useServicePlans(serviceId: string | null) {
  return useQuery({
    queryKey: ["service-plans", serviceId],
    enabled: !!serviceId,
    queryFn: async (): Promise<ServicePlan[]> => {
      const { data, error } = await supabase
        .from("service_plans")
        .select("*")
        .eq("service_id", serviceId!)
        .order("sort_order")
        .order("monthly_price_nok");
      if (error) throw error;
      return (data ?? []) as ServicePlan[];
    },
    staleTime: 1000 * 60 * 60,
  });
}

export function useSalary() {
  return useQuery({
    queryKey: ["salary"],
    queryFn: async (): Promise<SalaryEntry[]> => {
      const { data, error } = await supabase
        .from("salary_entries")
        .select("*")
        .order("effective_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as SalaryEntry[];
    },
  });
}

export function useInflation() {
  return useQuery({
    queryKey: ["inflation"],
    queryFn: async (): Promise<InflationRate[]> => {
      const { data, error } = await supabase
        .from("inflation_rates")
        .select("year, rate_percent, rate_kind, as_of_month")
        .order("year");
      if (error) throw error;
      return (data ?? []) as InflationRate[];
    },
    staleTime: 1000 * 60 * 60,
  });
}

export function useCurrencyRates() {
  return useQuery({
    queryKey: ["currency-rates"],
    queryFn: async (): Promise<CurrencyRate[]> => {
      const { data, error } = await supabase.from("currency_rates").select("*");
      if (error) throw error;
      return (data ?? []) as CurrencyRate[];
    },
    staleTime: 1000 * 60 * 60 * 12,
  });
}

export function toNokEquivalent(amount: number, currency: string, rates: CurrencyRate[]): number {
  const rate = rates.find((r) => r.currency === currency.toUpperCase());
  if (!rate) return amount;
  return Math.round(amount * rate.nok_per_unit * 100) / 100;
}

export function useProfile() {
  return useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", u.user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useUpsertSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Subscription> & { id?: string }) => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error("Ikke pålogget");

      let currency = input.currency?.toUpperCase();
      if (!currency) {
        if (input.id) {
          const { data: existing, error: fetchErr } = await supabase
            .from("subscriptions")
            .select("currency")
            .eq("id", input.id)
            .eq("user_id", u.user.id)
            .maybeSingle();
          if (fetchErr) throw fetchErr;
          currency = (existing?.currency || "NOK").toUpperCase();
        } else {
          currency = "NOK";
        }
      }

      const payload: Partial<Subscription> & { id?: string } = { ...input, currency };
      if (input.monthly_cost_nok != null) {
        const amount = Number(input.monthly_cost_nok);
        let nokEquiv = amount;
        if (currency !== "NOK") {
          const { data: rateRow, error: rateErr } = await supabase
            .from("currency_rates")
            .select("nok_per_unit")
            .eq("currency", currency)
            .maybeSingle();
          if (rateErr) throw rateErr;
          if (rateRow) {
            nokEquiv = Math.round(amount * Number(rateRow.nok_per_unit) * 100) / 100;
          }
        }
        payload.monthly_cost_nok = amount;
        payload.monthly_cost_nok_equiv = nokEquiv;
      }

      if (input.id) {
        const { id, ...rest } = payload;
        const { error } = await supabase
          .from("subscriptions")
          .update(rest)
          .eq("id", input.id)
          .eq("user_id", u.user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("subscriptions")
          .insert({ ...payload, user_id: u.user.id } as TablesInsert<"subscriptions">);
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["subscriptions"] }),
  });
}

export function useDeleteSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error("Ikke pålogget");
      const { error } = await supabase
        .from("subscriptions")
        .delete()
        .eq("id", id)
        .eq("user_id", u.user.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["subscriptions"] }),
  });
}

export function useUpsertSalary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<SalaryEntry> & { id?: string }) => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error("Ikke pålogget");
      if (input.id) {
        const { id, ...rest } = input;
        const { error } = await supabase
          .from("salary_entries")
          .update(rest)
          .eq("id", id)
          .eq("user_id", u.user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("salary_entries")
          .insert({ ...input, user_id: u.user.id } as TablesInsert<"salary_entries">);
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["salary"] }),
  });
}

export function useDeleteSalary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error("Ikke pålogget");
      const { error } = await supabase
        .from("salary_entries")
        .delete()
        .eq("id", id)
        .eq("user_id", u.user.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["salary"] }),
  });
}

// ============ Derived analytics ============

export interface SubscriptionTotals {
  monthly: number;
  yearly: number;
  activeCount: number;
  byCategory: { category: CategoryValue; monthly: number; share: number }[];
}

export function computeSubscriptionTotals(subs: Subscription[]): SubscriptionTotals {
  const active = subs.filter((s) => s.is_active);
  let monthly = 0;
  const cat = new Map<CategoryValue, number>();
  for (const s of active) {
    // IMPORTANT: sum monthly_cost_nok_equiv, never monthly_cost_nok directly.
    // monthly_cost_nok is in the subscription's own currency (see
    // s.currency) and is not safe to sum across rows of different
    // currencies. monthly_cost_nok_equiv is always NOK.
    const m = monthlyEquivalent(Number(s.monthly_cost_nok_equiv), s.billing_cycle);
    monthly += m;
    cat.set(s.category, (cat.get(s.category) ?? 0) + m);
  }
  const byCategory = Array.from(cat.entries())
    .map(([category, m]) => ({ category, monthly: m, share: monthly > 0 ? m / monthly : 0 }))
    .sort((a, b) => b.monthly - a.monthly);
  return { monthly, yearly: monthly * 12, activeCount: active.length, byCategory };
}

export interface SalaryGrowthRow {
  year: number;
  salary: number;
  growthPct: number | null;
  inflationPct: number | null;
  realGrowthPct: number | null;
  /** Which KPI methodology inflationPct came from for this year, so the
   * UI can mark a current-year figure as provisional rather than final.
   * Null if no inflation data exists for this year at all. */
  inflationKind: InflationRateKind | null;
}

/** Picks the best inflation row per year: arsvekst always wins over
 * tolvmanedersendring for the same year, since arsvekst is the final
 * official figure. tolvmanedersendring is only ever used as a fallback
 * for a year that doesn't have an arsvekst row yet (in practice, only
 * the current, not-yet-complete year). If a year somehow has more than
 * one tolvmanedersendring row, the one with the latest as_of_month wins. */
function bestInflationByYear(inflation: InflationRate[]): Map<number, InflationRate> {
  const byYear = new Map<number, InflationRate>();
  for (const i of inflation) {
    const existing = byYear.get(i.year);
    if (!existing) {
      byYear.set(i.year, i);
      continue;
    }
    if (existing.rate_kind === "arsvekst") continue; // never overwrite the final figure
    if (i.rate_kind === "arsvekst") {
      byYear.set(i.year, i);
    } else if ((i.as_of_month ?? 0) > (existing.as_of_month ?? 0)) {
      byYear.set(i.year, i);
    }
  }
  return byYear;
}

/** Real (inflation-adjusted) growth, computed as a ratio rather than a
 * simple subtraction: ((1 + nominal) / (1 + inflation)) - 1. This is
 * the correct way to combine two percentage rates and matches the
 * compounding methodology used elsewhere (e.g. the Stats Explorer's
 * cumulative real-growth figure) — using subtraction here instead would
 * silently disagree with that section for the same underlying concept. */
export function realGrowth(nominalPct: number, inflationPct: number): number {
  return ((1 + nominalPct / 100) / (1 + inflationPct / 100) - 1) * 100;
}

export function computeSalaryGrowth(
  entries: SalaryEntry[],
  inflation: InflationRate[],
): SalaryGrowthRow[] {
  // pick latest salary per year
  const byYear = new Map<number, number>();
  for (const e of [...entries].sort((a, b) => a.effective_date.localeCompare(b.effective_date))) {
    const y = new Date(e.effective_date).getFullYear();
    byYear.set(y, Number(e.gross_annual_nok));
  }
  const infl = bestInflationByYear(inflation);
  const entryYears = Array.from(byYear.keys()).sort();
  if (entryYears.length === 0) return [];
  // fill gaps with carry-forward salary so inflation is shown for every year
  const minY = entryYears[0];
  const maxY = entryYears[entryYears.length - 1];
  const rows: SalaryGrowthRow[] = [];
  let lastSal = byYear.get(minY)!;
  let prevSal: number | null = null;
  for (let y = minY; y <= maxY; y++) {
    const sal = byYear.has(y) ? byYear.get(y)! : lastSal;
    const growth = prevSal != null ? ((sal - prevSal) / prevSal) * 100 : null;
    const inflRow = infl.get(y);
    const cpi = inflRow ? Number(inflRow.rate_percent) : null;
    const real = growth != null && cpi != null ? realGrowth(growth, cpi) : null;
    rows.push({
      year: y,
      salary: sal,
      growthPct: growth,
      inflationPct: cpi,
      realGrowthPct: real,
      inflationKind: inflRow?.rate_kind ?? null,
    });
    prevSal = sal;
    lastSal = sal;
  }
  return rows;
}

/** Cumulative CPI factor from baseYear (inclusive) to targetYear (inclusive).
 * factor(base, base) = 1. factor(base, base+1) = 1 + cpi(base+1)/100.
 * Uses the best inflation row per year. Missing years are skipped (treated
 * as 0% growth) so we degrade gracefully rather than throw. */
export function cumulativeCpiFactor(
  inflation: InflationRate[],
  baseYear: number,
  targetYear: number,
): number {
  if (targetYear <= baseYear) return 1;
  const infl = bestInflationByYear(inflation);
  let f = 1;
  for (let y = baseYear + 1; y <= targetYear; y++) {
    const row = infl.get(y);
    if (row) f *= 1 + Number(row.rate_percent) / 100;
  }
  return f;
}

/** Nominal + real (base-year kroner) salary series for the chart. */
export interface NominalRealPoint {
  year: number;
  nominal: number;
  real: number;
}

export function computeNominalRealSeries(
  rows: SalaryGrowthRow[],
  inflation: InflationRate[],
): NominalRealPoint[] {
  if (rows.length === 0) return [];
  const baseYear = rows[0].year;
  return rows.map((r) => ({
    year: r.year,
    nominal: r.salary,
    real: r.salary / cumulativeCpiFactor(inflation, baseYear, r.year),
  }));
}
